import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2 * 60 * 1000; // check every 2 minutes

/**
 * Sits at the top of the app tree. Polls /api/version every 2 min. When the
 * backend's version changes (i.e. after a deploy or restart), shows a banner
 * asking the user to reload. One click triggers a hard reload that bypasses
 * cache — permanently fixes the "blank screen because stale JS bundle" issue.
 */
const VersionCheckBanner = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const baselineRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchVersion = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/version`, { timeout: 5000 });
        if (cancelled) return;
        const current = res.data?.version;
        if (!current) return;
        if (baselineRef.current === null) {
          baselineRef.current = current;
          return;
        }
        if (current !== baselineRef.current) {
          setUpdateAvailable(true);
        }
      } catch {
        // network hiccup — ignore silently, try again on next tick
      }
    };

    // Initial call captures baseline
    fetchVersion();
    const interval = setInterval(fetchVersion, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const handleReload = () => {
    // Hard reload — bypass browser cache
    if (window?.location?.reload) {
      window.location.reload(true);
    }
  };

  if (!updateAvailable) return null;

  return (
    <div
      data-testid="version-update-banner"
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 9999,
        backgroundColor: '#1e293b',
        color: '#fff',
        padding: '14px 20px',
        borderRadius: 12,
        boxShadow: '0 10px 30px rgba(15,23,42,0.3)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        maxWidth: 420,
        fontSize: 14,
        lineHeight: 1.4,
      }}
    >
      <div style={{ fontSize: 20 }}>🔄</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>A new version of FaciliTrack is available</div>
        <div style={{ opacity: 0.8, fontSize: 12, marginTop: 2 }}>
          Reload to get the latest fixes.
        </div>
      </div>
      <button
        data-testid="version-update-reload-btn"
        onClick={handleReload}
        style={{
          background: '#22c55e',
          color: '#fff',
          border: 'none',
          padding: '8px 16px',
          borderRadius: 8,
          fontWeight: 600,
          cursor: 'pointer',
          fontSize: 13,
        }}
      >
        Reload
      </button>
    </div>
  );
};

export default VersionCheckBanner;
